import {Injectable} from '@angular/core';
import {Observable, of, Subscription, throwError} from 'rxjs';
import {catchError, map, take} from 'rxjs/operators';
import {HttpClient} from '@angular/common/http';
import {BaseDataService} from '../../core/services/base-data.service';
import {HupSubscription} from '../models/subscription.model';
import {BaseData} from '../models/base-data.model';
import {OfferService} from "./offer.service";
import {DatePipe} from "@angular/common";
import {PaymentTransactionModel} from "../../core/components/onlinepayment/paymentTransaction.model";
import {ValidDate} from "../models/valid-date.model";
import {AddressListItem} from "../models/address-list.model";
import {MessageSearchResult} from "../models/message-search-result";
import {OfferPrice} from "../models/price.model";
import {ProductOrderedCheckResponse} from "../models/productOrderedCheck/product-ordered-response.model";
import {Offer} from "../models/offer.model";
import {UserData} from "../models/user-data.model";
import {Qualified4CombinationSubscription} from "../models/qualified-subscription.model";

type WholeResponseResult = {
  headers?: {[key: string]: string}[],
  body: any
}

@Injectable({providedIn: 'root'})
export class HupSubscriptionsService {
  public static readonly COOKIE_NAME: string = 'webabo-subscription-cookie';

  products: BaseData[] = [];
  subToPrice: Map<number, OfferPrice[]> = new Map<number, OfferPrice[]>();
  loadedAll = false;
  private subs: HupSubscription[] = [];
  private subsWithTerminated: HupSubscription[] = [];

  // for reference
  // states: {
  //   '-1': 'new',
  //   0: 'changed',
  //   1: 'actual',
  //   2: 'outdated',
  //   3: 'canceled',
  //   4: 'compensation',
  //   5: 'epaperreplacement',
  // };

  constructor(
    private http: HttpClient,
    private baseDataService: BaseDataService,
    private datePipe: DatePipe
  ) {
  }

  getSubscriptionsAlsoTerminated(): Observable<HupSubscription[]> {
    const salt = new Date().getTime();
    return this.http.get<HupSubscription[]>('/webabo/subscriptionsAlsoTerminated?' + salt).pipe(
      map(subs => {
        subs.forEach((sub) => {
          this.handleSubscription(sub);
        });
        this.subsWithTerminated = subs;
        return this.subsWithTerminated;
      }));
  }

  getSubscriptionsGroupedAlsoTerminated(): Observable<HupSubscription[]> {
    const salt = new Date().getTime();
    return this.http.get<HupSubscription[]>('/webabo/subscriptionsForOverviewAlsoTerminated?' + salt).pipe(
      map(subs => {
        subs.forEach((sub) => {
          this.handleSubscription(sub);
        });
        this.subsWithTerminated = subs;
        return this.subsWithTerminated;
      }));
  }

  getSubscriptions(onlyRevocable: boolean = false): Observable<HupSubscription[]> {
    const salt = new Date().getTime();
    // Der Query-Param muss exakt so heißen wie der Controller-Parameter (SubscriptionController.getSubscriptionList)
    return this.http.get<HupSubscription[]>('/webabo/subscriptions?' + salt, { params: {'onlyRevocable': onlyRevocable} }).pipe(
      map(subs => {
        this.loadedAll = true;
        subs.forEach((sub) => {
          this.handleSubscription(sub);
        });
        this.subs = subs;
        return this.subs;
      }));
  }

  getSubscriptionsUnauthorized(backendId: number): Observable<HupSubscription[]> {
    return this.http.get<HupSubscription[]>('/webabo/subscriptions/unauthorized' + '?userBackendId=' + backendId).pipe(
      map(subs => {
        subs.forEach(sub => {
          sub.userBackendId = backendId;
          this.handleSubscription(sub);
        });
        return subs;
      }));
  }

  getSubscription(webId: string, backendId: string): Observable<HupSubscription> {
    const salt = new Date().getTime();
    return this.http.get<HupSubscription>('/webabo/subscriptions/' + webId, {params: {backendId, salt: salt}}).pipe(
      map(sub => {
        sub = sub instanceof Array ? sub[0] : sub;
        this.handleSubscription(sub);
        this.subs.push(sub);
        return sub;
      }));
  }

  getSubscriptionForDetail(webId: string, backendId: string): Observable<HupSubscription> {
    const salt = new Date().getTime();
    return this.http.get<HupSubscription>('/webabo/subscriptions/' + webId + '/aggregated', {params: {backendId, salt: salt.toString()}}).pipe(
      map(sub => {
        sub = sub instanceof Array ? sub[0] : sub;
        this.handleSubscription(sub);
        sub.sequelSubscriptions?.forEach(seq => {
          this.handleSubscription(seq);
          seq.combiSubscriptionList?.forEach(seqComb => {
            this.handleSubscription(seqComb);
          });
        });
        sub.combiSubscriptionList?.forEach(comb => {
          this.handleSubscription(comb);
        });
        return sub;
      }));
  }

  getDelayMessages(backendId: string): Observable<MessageSearchResult[]> {
    return this.http.get<MessageSearchResult[]>('/webabo/subscriptions/' + backendId + '/delaymessages').pipe(
      map(messages => {
        return messages;
      }));
  }

  handleSubscription(sub: HupSubscription): void {

    this.handleState(sub);
    const products = this.baseDataService.getBaseData('products');

    if (!products || products.length == 0) {
      this.baseDataService.loadBaseData('products').subscribe(productList => {
        this.handleProducts(productList, sub);
      });
    } else {
      this.handleProducts(products, sub);
    }

    if (sub.offer) {
      sub.readOnly = (!sub.offer || sub.offer.price === 0 || sub.state >= 2);
      if (sub?.offer?.product?.productCode) {
        sub.productCode = sub.offer.product.productCode;
        this.handleProducts(products, sub);
      }
    }


    sub.validDate.validFrom = new Date(sub.validDate.validFrom);
    if (ValidDate.isEndless(sub.validDate.validUntil) || sub.validDate.validUntil == null) {
      sub.validDate.validUntil = null;
    } else {
      sub.validDate.validUntil = new Date(sub.validDate.validUntil);
    }


    this.baseDataService.loadBaseData('variantcode/' + sub.variantCode).subscribe(
      (codes: any) => {
        if (codes) {
          if (codes.length > 0) {
            sub.variantDescription = codes[0].description;
          } else {
            sub.variantDescription = codes.description;
          }
        }
      }
    );

    this.setCurrentFromList(sub, 'curPayment', sub.paymentList);
    this.setCurrentFromList(sub, 'curDelivery', sub.deliveryAddressList);
    this.setCurrentFromList(sub, 'curBilling', sub.billingAddressList);

    sub.subId = sub.backendId;

/*
    this.offerService.getOfferDetail(sub.offerId).subscribe(
      (offer: Offer) => {
        if (offer) {
          sub.offer = offer;
          // isReadOnly aus SubscriptionController Webabo 2.3 - gekürzt
          sub.readOnly = (!sub.offer || sub.offer.price === 0 || sub.state >= 2);
          sub.productCode = offer.product.productCode;
          this.handleProducts(products, sub);
        }
      }
    )*/
  }

  handleState(sub: HupSubscription): void {
    const today = new Date();
    const validFrom = new Date(sub.validDate.validFrom);
    const validUntil = new Date(sub.validDate.validUntil);
    if(sub.webId > 0) {
      // liegt in der WebDB
      sub.state = -1;
    } else if (validFrom <= today && validUntil >= today) {
      // Ist aktiv
      sub.state = 0;
    } else if (validUntil < today) {
      // Ist beendet
      sub.state = 3;
    } else if (validFrom > today) {
      // Ist zukünftig
      sub.state = 1;
    }
  }

  handleProducts(products, sub): void {
    if (products) {
      for (const product of products) {
        if (product.key === sub.productCode) {
          sub.productDescription = product.description;
          break;
        }
      }
    }
  }

  setCurrentFromList(sub: HupSubscription, type: string, list): void {
    const curDate = new Date();
    for (const item of list) {
      if (item.validDate && item.validDate.validFrom) {
        // const validFrom = new Date(item.validDate.validFrom);
        let validUntil = new Date('2999-01-01');
        if (item.validDate.validUntil) {
          validUntil = new Date(item.validDate.validUntil);
        }
        // Davon ausgehend, dass alle Daten chronologisch sortiert sind.
        // (Das erste Datum kann auch in der Zukunft sein -> ist dann trotzdem aktuell)
        if (validUntil >= curDate) { // OR ENDLESS!
          if (item.person && item.address) {
            this.baseDataService.handleAddress(item.address);
            this.baseDataService.handlePerson(item.person);
          }
          sub[type] = item;
          return;
        }
      } else {
        sub[type] = item;
      }
    }
  }

  sendOffer(data): Observable<any> {
    return this.http.post('/webabo/subscriptions', data).pipe(map((result) => {
      this.resetData();
      return result;
    }));
  }

  sendSubscriptionUnauthorized(data): Observable<any> {
    return this.http.post('/webabo/subscriptions/unauthorized', data, {observe: 'response'})
      .pipe(map((result) => {
        this.resetData();
        let rslt: WholeResponseResult = {body: result.body};
        if (result.headers.get('x-otlc').length) {
          rslt['otlc'] = result.headers.get('x-otlc');
        }
        return rslt;
      }));
  }

  plausiCheck(data): Observable<any> {
    return this.http.post('/webabo/subscriptions/plausi', data).pipe(map(data => {
      return data;
    }));
  }

  sendSubPaymentEdit(subId: number, paymentId: number, data): Observable<any> {
    const url = '/webabo/subscriptions/' + subId + '/payments/' + paymentId;
    return this.http.patch(url, data, {headers: {'Content-Type': 'application/merge-patch+json'}}).pipe(map(() => {
      this.resetData();
    }));
  }

  getNextDeliveryDate(productCode: string, variantCode: string): Observable<Date> {
    return this.http.get<any>('/webabo/subscriptions/unauthorized/next-delivery-date?productCode=' + productCode + '&variantCode=' + variantCode).pipe(map(data => {
      return new Date(data);
    }));
  }

  getPriceForSubscription(subscription): Observable<any> {
    return this.http.post('/webabo/subscriptions/prices', subscription).pipe(
      map(data => {
        return data;
      }),
      catchError(error => {
        return throwError(error)
      })
    );
  }

  public resetData(): void {
    this.loadedAll = false;
    this.subs = [];
    this.subsWithTerminated = [];
  }

  getTerminationDateOfSubscription(subId, terminationType): Observable<any> {
    const url = '/webabo/subscriptions/unauthorized/next-termination-date' +
      '?backendId=' + subId +
      '&reason=' + terminationType;
    return this.http.get(url).pipe(map((result) => {
        return result;
      })
    );
  }

  terminateSubscription(webId: number, subId: number, terminationType: string, terminationDate: Date): Observable<any> {

    const url = '/webabo/subscriptions/' +
      webId +
      '?backendId=' + subId +
      '&reason=' + terminationType +
      '&terminationDate=' + this.datePipe.transform(terminationDate, 'yyyy-MM-dd');

    return this.http.delete(url).pipe(map((result) => {
        this.resetData();
        return result;
      })
    );
  }

  startBuckarooPaymentTransaction(paymentTransaction: PaymentTransactionModel): Observable<any> {
    const url = '/webabo/buckaroo?amount=' + paymentTransaction.amount + '&invoice=' + paymentTransaction.invoiceText + '&wssId=' + paymentTransaction.wssId;
    return this.http.post(url, JSON.stringify(paymentTransaction.orderObject)).pipe(map((result) => {
        return result;
      })
    );
  }

  getCustomerUnauthorized(clientno): Observable<AddressListItem> {
    const url = '/webabo/users/unauthorized/' + clientno;
    return this.http.get<AddressListItem>(url).pipe(map((result) => {
        return result
      }),
      catchError(error => {
        return of(null);
      }));
  }

  fillCustomerUnauthorized(user): Observable<AddressListItem[]> {
    const url = '/webabo/users/searchAndFill?strictSearch=false';
    return this.http.post<AddressListItem[]>(url, user).pipe(map((result) => {
        return result
      }),
      catchError(error => {
        return of(null);
      }));
  }

  getNextChangeDate( orderNo: number): Observable<any> {
    const url = '/webabo/subscriptions/' + orderNo + 'next-change-date';
    return this.http.get<Date>(url).pipe(map((result) => {
        return result
      }),
      catchError(error => {
        return of(null);
      }));
  }

  setPriceForKey(id: number, price: OfferPrice[]) : void {
    this.subToPrice.set(id, price);
  }

  getPriceForKey(id: number) : OfferPrice[] {
    const price: OfferPrice[] = this.subToPrice.get(id)?? []
    this.subToPrice.delete(id);
    return price;
  }

  checkIfCurrentProductAlreadyOrdered(userBackendId: number, productCode: string, orderStartDate: string): Observable<ProductOrderedCheckResponse> {
    return this.http.post<ProductOrderedCheckResponse>(`webabo/subscriptions/has-product`, {
      userBackendId: userBackendId,
      productCode: productCode,
      orderStartDate: orderStartDate
    })
      .pipe(
        map(response => response)
      );
  }

  /**
   * Die Abonnements des angemeldeten Kunden, die um Kombi-Angebote erweitert werden können - je
   * Abonnement mit den aktuell zubuchbaren Kombi-Werbeschlüsseln.
   *
   * Das Salt ist hier nicht schmückendes Beiwerk: die Liste entwertet sich durch eine Zubuchung
   * auf einer Folgeseite selbst, und pca schickt zu dieser Antwort keinerlei Cache-Header. Ohne
   * Salt darf der Browser sie also wiederverwenden und zeigt nach der Rückkehr ein Angebot an,
   * das gar nicht mehr zubuchbar ist. Dieselbe Vorsichtsmaßnahme tragen alle übrigen Abo-Abrufe
   * dieses Service.
   */
  getQualifiedSubscriptions(): Observable<Qualified4CombinationSubscription[]> {
    const url = '/webabo/subscriptions/qualified-for-combi-subscription';
    const salt = new Date().getTime();

    return this.http.get<Qualified4CombinationSubscription[]>(url, {params: {salt: salt.toString()}}).pipe(
      map(qualifiedSubs => {
        qualifiedSubs.forEach(qualified => this.handleSubscription(qualified.subscription));
        return qualifiedSubs;
      }),
      catchError(error => {
        console.error('Zubuchbare Abonnements konnten nicht geladen werden', error);
        return throwError(error);
      }));
  }

  /**
   * Welche der gewünschten Kombi-Werbeschlüssel sind zu diesem einen Abonnement noch zubuchbar?
   *
   * Salt wie bei {@link getQualifiedSubscriptions} - hier wiegt eine gecachte Antwort sogar
   * schwerer: ein veraltetes Angebot würde dem Kunden das Zubuchen anbieten, obwohl es nicht mehr
   * möglich ist. Jeder Fehler endet in "nichts zubuchbar" - ohne belastbare Auskunft wird nichts
   * angeboten.
   */
  getQualifiedSubscription(subId: string, salesCodeIds: string[]): Observable<Qualified4CombinationSubscription> {
    const url = `/webabo/subscriptions/${subId}/qualified-for-combi-subscription`;
    const salt = new Date().getTime();
    const notQualified: Qualified4CombinationSubscription = {subscription: null, bookableCombiOffers: []};

    return this.http.get<Qualified4CombinationSubscription>(url, {
      params: {salesCodeIds: salesCodeIds, salt: salt.toString()}
    }).pipe(
      map(qualified => {
        if (!qualified?.subscription || !qualified.bookableCombiOffers?.length) {
          return notQualified;
        }
        this.handleSubscription(qualified.subscription);
        return qualified;
      }),
      catchError(error => {
        console.error(`Prüfung auf Zubuchbarkeit für Abonnement ${subId} fehlgeschlagen`, error);
        return of(notQualified);
      }));
  }

  /**
   * Legt zum Hauptabonnement je Kombi-Werbeschlüssel ein Kombi-Abo (Zubucher) an.
   * pca prüft serverseitig, dass die userBackendId zum angemeldeten Nutzer gehört und dass jeder
   * Werbeschlüssel optional zum Werbeschlüssel des Hauptabos ist.
   */
  createCombiSubscriptions(userBackendId: number, mainSubscriptionId: number, salesCodeIds: string[]): Observable<HupSubscription[]> {
    const url = '/webabo/subscriptions/combi-subscription';

    return this.http.post<HupSubscription[]>(url, {userBackendId, mainSubscriptionId, salesCodeIds}).pipe(
      map(subs => {
        this.resetData();
        return subs;
      }),
      catchError(error => {
        console.error(`Kombi-Abonnements zu Abonnement ${mainSubscriptionId} konnten nicht angelegt werden`, error);
        return throwError(error);
      }));
  }
}
